import { readEntraConfig } from './entraConfig';
import { createStaffAuth, createMsalClient } from './staffAuth';
import { createClubSession } from './clubSession';
import { createSessionChannel } from './sessionChannel';
import { createMemberSession } from './memberSession';
import { endCustomerSessions } from './signOutAll';

// The app-wide staff auth instance.
//
// Built from `import.meta.env` once, at module evaluation, mirroring how
// Storage picks its backend — one decision, made in one place, that the rest
// of the app reads rather than re-derives. When the VITE_ENTRA_* variables are
// unset (which is everywhere today) this resolves to `disabled` and no MSAL
// code is ever loaded: the import inside createMsalClient is dynamic, so the
// library is not even in the initial bundle.
// NAMED READS, NEVER THE WHOLE OBJECT.
//
// `import.meta.env` referenced as an object makes Vite inline EVERY VITE_*
// variable into the bundle as one literal — this line alone put the Supabase
// URL and anon key into an API-only build, complete with a working legacy
// backend behind them. Blanking the `define` tokens in vite.config.ts did not
// help and could not: these values arrive by a different road.
//
// Vite can only replace what it can see statically, so the four variables this
// actually needs are read by name. Adding a fifth means adding it here — which
// is the point: what ships is a decision, not a side effect.
const clientEnv: Record<string, string | undefined> = {
  VITE_ENTRA_CLIENT_ID: import.meta.env.VITE_ENTRA_CLIENT_ID,
  VITE_ENTRA_TENANT_ID: import.meta.env.VITE_ENTRA_TENANT_ID,
  VITE_ENTRA_API_SCOPE: import.meta.env.VITE_ENTRA_API_SCOPE,
  VITE_ENTRA_REDIRECT_URI: import.meta.env.VITE_ENTRA_REDIRECT_URI,
};

export const entraConfigStatus = readEntraConfig(
  clientEnv,
  typeof window !== 'undefined' ? window.location.origin : '',
);

export const staffAuth = createStaffAuth(entraConfigStatus, createMsalClient);

// The app-wide CLUB PORTAL session (#186).
//
// Separate instance from `staffAuth` because they are separate subjects: a club
// administrator is a customer with an account in our database, a Starpoint
// staff member is a directory identity. The transport prefers this one — see
// lib/api/http.ts.
// THE REAL CROSS-TAB CHANNEL IS WIRED HERE, and only here (#773).
//
// A step-up rotates this tab's bearer and the server revokes the previous one,
// so another tab holding it would send a dead credential, get 401 and sign
// itself out — stepping up in one tab logged you out of the others. The notice
// carries no token: the step-up already re-issued the shared HttpOnly cookie,
// so the other tabs need only stop suppressing it with a stale bearer.
//
// `createClubSession` defaults to an inert channel, so this line is what turns
// the coordination on for the real app — see the comment there for why the
// default is not the live one.
export const clubSession = createClubSession({ channel: createSessionChannel() });

// The app-wide MEMBER session (#185).
//
// A third subject, separate from both: a player signing in with their club's
// join code is neither a club administrator nor Starpoint staff. The transport
// prefers the club session when one exists — a browser is the portal or the
// member app, not both — and falls back to this.
export const memberSession = createMemberSession();

// ── ONE CONTROL, EVERY CUSTOMER SESSION (#752) ─────────────────────
//
// The app-wide pairing of the two above. `signOutAll.ts` carries the reasoning
// and the testable core; this is the list, and it lives here because this file
// is already the one place that knows which customer sessions exist.
//
// Staff auth is NOT in it, deliberately: an Entra session is the directory's,
// not the portal's to revoke, and `handleLogout` already says so for the
// impersonation case. This ends the sessions Starpoint issued.
export function signOutCustomerSessions(): Promise<void> {
  return endCustomerSessions([clubSession, memberSession]);
}
